// Accordion.jsx
import React, { useContext } from 'react';
import PropTypes from 'prop-types';

const Context = React.createContext(null);

export const Accordion = React.forwardRef(
  ({ as: Component, activeKey, children, className }, ref) => {
    return (
      <Context.Provider value={activeKey}>
        <Component ref={ref} className={className}>
          {children}
        </Component>
      </Context.Provider>
    );
  }
);

Accordion.displayName = 'Accordion';
Accordion.propTypes = {
  as: PropTypes.elementType,
  activeKey: PropTypes.string,
  children: PropTypes.node,
  className: PropTypes.string,
};

Accordion.defaultProps = {
  as: 'div',
  activeKey: '',
  children: null,
  className: undefined,
};

export const Collapse = React.forwardRef(
  ({ as: Component, className, children, eventKey }, ref) => {
    const contextEventKey = useContext(Context);
    const isActive = contextEventKey === eventKey;
    const style = isActive
      ? { overflow: 'hidden' }
      : { maxHeight: 0, overflow: 'hidden', padding: 0 };
    return (
      <Component
        ref={ref}
        className={className}
        style={style}
        selected={isActive}
      >
        {children}
      </Component>
    );
  }
);

Collapse.displayName = 'AccordionCollapse';
Collapse.propTypes = {
  as: PropTypes.elementType,
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  eventKey: PropTypes.string.isRequired,
};

Collapse.defaultProps = {
  as: 'div',
  className: undefined,
};

export const Toggle = React.forwardRef(
  ({ as: Component, eventKey, onClick, children, ...restProps }, ref) => {
    const contextEventKey = useContext(Context);
    const isActive = contextEventKey === eventKey;
    return (
      <Component
        ref={ref}
        selected={isActive}
        data-element_id={restProps?.['data-element_id']}
        data-tracking_payload={restProps?.['data-tracking_payload']}
        onClick={event => {
          const eventKeyPassed = eventKey === contextEventKey ? null : eventKey;
          onClick(eventKeyPassed, event);
        }}
      >
        {children}
      </Component>
    );
  }
);

Toggle.displayName = 'AccordionToggle';
Toggle.propTypes = {
  as: PropTypes.elementType,
  eventKey: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  onClick: PropTypes.func,
};

Toggle.defaultProps = {
  as: 'div',
  onClick: () => null,
};

export default Accordion;
